import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FileText, Loader2, Search } from "lucide-react";
import { getStoredUser } from "../services/authSessionService";
import { getDraftFileBase64 } from "../services/generatedContractService";
import { getKbGeneratedDocumentStoredFileId } from "../services/gameRuleGeneratedDocumentsService";
import { loadWorkspaceRows } from "../services/gameRuleWorkspaceService";
import { openDraftInHost } from "../services/hostDocumentService";
import type { GameRuleWorkspaceRow, WorkspaceStatusFilter } from "../types/gameRuleWorkspace";
import { formatIsoDateOnly } from "../utils/displayDate";
import { getPrizeLogicListingPrimaryAction } from "../utils/prizeLogicListingActions";

const LIST_LIMIT = 100;

type ListingLocationState = {
  readonly generateSuccessMessage?: string;
};

const STATUS_FILTERS: readonly {
  readonly id: WorkspaceStatusFilter;
  readonly label: string;
}[] = [
  { id: "all", label: "All" },
  { id: "in_progress", label: "In progress" },
  { id: "myqueue", label: "My review queue" },
  { id: "review", label: "Pending review" },
  { id: "approved", label: "Approved" },
];

export const PrizeLogicListingPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const locationState = location.state as ListingLocationState | null;
  const [successMessage, setSuccessMessage] = useState<string | null>(
    locationState?.generateSuccessMessage ?? null,
  );
  const [rows, setRows] = useState<readonly GameRuleWorkspaceRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<WorkspaceStatusFilter>("all");
  const [openingId, setOpeningId] = useState<string | null>(null);
  const [openingMode, setOpeningMode] = useState<"open" | "review" | null>(null);

  const currentUserEmail = useMemo(() => getStoredUser()?.email ?? null, []);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    loadWorkspaceRows({
      search: appliedSearch.trim() || undefined,
      statusFilter,
      currentUserEmail,
      documentLimit: LIST_LIMIT,
      sessionLimit: LIST_LIMIT,
    })
      .then((items) => setRows(items))
      .catch((e: unknown) => {
        setError(e instanceof Error ? e.message : "Failed to load documents.");
      })
      .finally(() => setLoading(false));
  }, [appliedSearch, statusFilter, currentUserEmail]);

  useEffect(() => {
    void load();
  }, [load]);

  const openInWord = useCallback(async (row: GameRuleWorkspaceRow): Promise<void> => {
    const storedFileId = await getKbGeneratedDocumentStoredFileId(row.id);
    const base64 = await getDraftFileBase64(storedFileId);
    await openDraftInHost(base64);
  }, []);

  const openDocument = useCallback(
    async (row: GameRuleWorkspaceRow) => {
      setOpeningId(row.id);
      setOpeningMode("open");
      setError(null);
      try {
        await openInWord(row);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Could not open document in Word.");
      } finally {
        setOpeningId(null);
        setOpeningMode(null);
      }
    },
    [openInWord],
  );

  const startReview = useCallback(
    async (row: GameRuleWorkspaceRow) => {
      setOpeningId(row.id);
      setOpeningMode("review");
      setError(null);
      try {
        await openInWord(row);
        navigate(`/prizelogic/generated-documents/${encodeURIComponent(row.id)}/review`, {
          state: {
            openedInWord: true,
            documentName: row.name,
          },
        });
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : "Could not open document for review.");
      } finally {
        setOpeningId(null);
        setOpeningMode(null);
      }
    },
    [navigate, openInWord],
  );

  const resumeSession = useCallback(
    (sessionId: string) => {
      navigate("/prizelogic/generate-document/wizard", {
        state: { resumeSessionId: sessionId },
      });
    },
    [navigate],
  );

  const handlePrimaryAction = useCallback(
    (row: GameRuleWorkspaceRow) => {
      const action = getPrizeLogicListingPrimaryAction(row, currentUserEmail);
      if (action === "Resume") {
        resumeSession(row.id);
        return;
      }
      if (action === "Review") {
        void startReview(row);
        return;
      }
      void openDocument(row);
    },
    [currentUserEmail, openDocument, resumeSession, startReview],
  );

  const openingBusy = openingId !== null;
  const openingLabel =
    openingMode === "review" ? "Opening for review…" : "Opening document…";

  return (
    <div className="relative space-y-4">
      {openingBusy ? (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-background/85 backdrop-blur-[2px]"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <div className="flex flex-col items-center gap-3 rounded-lg border border-[var(--border)] bg-card px-8 py-6 shadow-lg">
            <Loader2 className="h-9 w-9 animate-spin text-primary" aria-hidden />
            <p className="text-sm font-medium text-foreground">{openingLabel}</p>
            <p className="max-w-xs text-center text-xs text-muted-foreground">Loading file into Word.</p>
          </div>
        </div>
      ) : null}
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-foreground">Generated Documents</h2>
          <p className="text-sm text-muted-foreground mt-1">
            In-progress drafts and completed game rules. Resume a draft or open a document for
            review.
          </p>
        </div>
        <Link to="/prizelogic" className="text-sm font-medium text-primary hover:underline shrink-0">
          Back
        </Link>
      </div>
      {successMessage ? (
        <div className="rounded-lg border border-emerald-300 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          {successMessage}
          <button
            type="button"
            className="ml-2 underline"
            onClick={() => setSuccessMessage(null)}
          >
            Dismiss
          </button>
        </div>
      ) : null}
      {error ? (
        <div className="rounded-lg border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive">
          {error}
          <button type="button" className="ml-2 underline" onClick={() => void load()}>
            Retry
          </button>
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2 items-stretch">
        <div className="relative min-w-0 flex-1 max-w-md">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <input
            type="search"
            placeholder="Search by name, file, reviewer, or status…"
            disabled={openingBusy}
            className="w-full rounded-lg border border-input bg-background py-2.5 pl-9 pr-3 text-sm disabled:opacity-50"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                if (!openingBusy) setAppliedSearch(searchInput);
              }
            }}
          />
        </div>
        <button
          type="button"
          disabled={openingBusy}
          className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-medium text-primary hover:bg-accent disabled:opacity-50"
          onClick={() => setAppliedSearch(searchInput)}
        >
          Search
        </button>
      </div>
      <div className="flex flex-wrap gap-2">
        {STATUS_FILTERS.map((filter) => (
          <button
            key={filter.id}
            type="button"
            disabled={openingBusy}
            className={`rounded-full px-3 py-1 text-xs font-semibold border transition-colors disabled:opacity-50 ${
              statusFilter === filter.id
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background text-muted-foreground border-input hover:bg-accent"
            }`}
            onClick={() => setStatusFilter(filter.id)}
          >
            {filter.label}
          </button>
        ))}
      </div>
      <div className="rounded-lg border border-[var(--border)] bg-card divide-y divide-border overflow-hidden">
        {loading ? (
          <p className="py-8 text-center text-sm text-muted-foreground">Loading…</p>
        ) : rows.length === 0 ? (
          <p className="py-8 text-center text-sm text-muted-foreground">
            No items match your search or filter. Start a new game rule from Generate Document.
          </p>
        ) : (
          rows.map((row) => {
            const dateStr = formatIsoDateOnly(row.updatedAt);
            const metaParts = [
              row.statusLabel,
              `${row.sectionCount} section${row.sectionCount === 1 ? "" : "s"}`,
              dateStr,
            ];
            if (row.reviewerEmail) {
              metaParts.splice(1, 0, row.reviewerEmail);
            }
            const rowKey = `${row.kind}-${row.id}`;
            const primaryAction = getPrizeLogicListingPrimaryAction(row, currentUserEmail);
            return (
              <div
                key={rowKey}
                className="flex items-center gap-3 px-3 py-3 hover:bg-muted/40"
              >
                <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                <button
                  type="button"
                  disabled={openingBusy}
                  className="min-w-0 flex-1 flex flex-col items-stretch gap-0.5 text-left disabled:opacity-50 disabled:pointer-events-none"
                  onClick={() => handlePrimaryAction(row)}
                >
                  <span className="font-semibold text-foreground text-sm leading-snug">
                    {row.name}
                  </span>
                  <span className="text-xs text-muted-foreground truncate">{row.subtitle}</span>
                  <span className="text-xs text-muted-foreground">{metaParts.join(" · ")}</span>
                </button>
                <button
                  type="button"
                  disabled={openingBusy}
                  className="shrink-0 rounded-md border border-input bg-background px-3 py-1.5 text-xs font-semibold text-primary hover:bg-accent disabled:opacity-50"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrimaryAction(row);
                  }}
                >
                  {primaryAction}
                </button>
              </div>
            );
          })
        )}
      </div>
      <div className="flex flex-wrap gap-2 justify-end">
        <Link
          to="/prizelogic/generate-document"
          className="rounded-lg border border-input bg-background px-4 py-2 text-sm font-semibold text-primary hover:bg-accent"
        >
          Generate new
        </Link>
      </div>
    </div>
  );
};
